/**
 * MemoCover — the GOLDEN investment-memo title page (golden-convergence GC-W2).
 *
 * A faithful reproduction of the hand-built golden reference
 * (`Redhead Industrial Park — BTM Feasibility (Draft).pdf`): a humanist-SANS
 * memorandum cover, NOT the editorial-monograph masthead. Anatomy, top→bottom:
 *   - brand LOGO top-left + a top-right meta block (CONFIDENTIAL in the negative
 *     role · the type-aware SERIES label · "PREPARED <Month Year>");
 *   - a full-width hairline rule;
 *   - the tracked MEMORANDUM label (type-aware);
 *   - the large bold site-name TITLE + an italic one-line subtitle;
 *   - a SITE / SPONSOR two-column meta block;
 *   - the HEADLINE VERDICT box — a tier-coloured LEFT-border filled panel with
 *     the big verdict word + an italic description (vs the editorial cover's
 *     outlined pill);
 *   - a 4-up bordered METRIC strip (the headline KPI chips);
 *   - a faint "DRAFT" watermark (draft reports only);
 *   - a "BRAND · SITE" footer.
 *
 * Theme-aware + null-safe like the other covers: every colour is a roleColor
 * token (the `memo` preset supplies the golden sans + teal accent), title/
 * subtitle/verdict/metrics all read EXISTING persisted report+theme data via the
 * shared journal/KPI/verdict resolvers, and an absent field collapses its block
 * rather than throwing. It is the DEFAULT cover of the `memo` preset / the stock
 * default template; it never resolves elsewhere.
 */
import React from 'react';
import {
  resolveCoverTitle,
  resolveCoverKicker,
  resolveSiteWordmark,
  resolveBylineParts,
} from '../../../../lib/reportRendering/journalCoverData';
import { resolveCoverChipsForReport, type CoverChip } from '../../../../lib/reportRendering/coverKpiChips';
import { DEFAULT_PRIMARY_COVER_LOGO_URL } from '../../../../lib/reportBranding';
import { roleColor, roleColorAlpha } from '../../../../lib/reportRoleColors';
import { reportTypographySizes, reportHeadingInk } from '../../../../lib/reportSectionHeading';
import { COVER_MIN_HEIGHT_PX } from '../../../../lib/reportCoverTokens';
import { resolveCoverVerdict, type VerdictTier } from '../../../../lib/reportVerdict';
import { REPORT_COVER_DISCLAIMER } from '../../../../lib/reportLegalDisclaimer';
import type { ReportTypeId } from '../../../../config/reportTypes';
import { CoverHeaderPlatformCredit, platformCreditToneForTheme } from '../ReportPlatformCredit';
import type { CoverProps } from './types';

/** Map a verdict tier to a role-colour key for the headline verdict box. */
function verdictBoxRole(tier: VerdictTier): 'positive' | 'warning' | 'negative' | 'muted' {
  switch (tier) {
    case 'green': return 'positive';
    case 'amber': return 'warning';
    case 'red': return 'negative';
    default: return 'muted';
  }
}

/** Type-aware tracked memorandum label (golden: "BEHIND-THE-METER POWER
 *  FEASIBILITY MEMORANDUM"). Falls back to a generic feasibility memorandum. */
const MEMO_LABEL_BY_TYPE: Partial<Record<ReportTypeId, string>> = {
  feasibility: 'Behind-the-Meter Power Feasibility Memorandum',
  'power-availability': 'Power Availability & BTM Memorandum',
  interconnection: 'Grid Interconnection Memorandum',
  'water-resource': 'Water & Cooling Resource Memorandum',
  environmental: 'Environmental & Permitting Memorandum',
  'test-fit': 'Site Test-Fit Memorandum',
  'supply-chain-risk': 'Supply-Chain Risk Memorandum',
  'public-sentiment': 'Public Sentiment Memorandum',
  financial: 'Financial Viability Memorandum',
  'executive-summary': 'Executive Decision Memorandum',
  'custom-brief': 'Investment Memorandum',
};

/** First non-empty line of a multi-line field. */
function firstLine(value: string | undefined | null): string | null {
  if (!value || !value.trim()) return null;
  const line = value.split('\n').map((p) => p.trim()).find(Boolean);
  return line ?? null;
}

/** "PREPARED" imprint — persisted createdAt, else now; never throws. */
function resolvePreparedDate(report: CoverProps['report']): string {
  const raw = report.createdAt;
  const parsed = raw ? new Date(raw) : new Date();
  const date = Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

export const MemoCover: React.FC<CoverProps> = ({ report, theme, branding, disclaimer }) => {
  const effectiveBranding = branding ?? {
    audienceMode: 'internal' as const,
    siteWordmark: resolveSiteWordmark(report, undefined),
    primaryLogoUrl: theme.logoUrl ?? DEFAULT_PRIMARY_COVER_LOGO_URL,
    secondaryLogoUrl: null,
    primaryLogoAlt: theme.companyName ?? 'Primary logo',
    secondaryLogoAlt: 'Customer logo',
  };

  const reportType: ReportTypeId = report.reportType ?? 'feasibility';
  const title = resolveCoverTitle(report);
  const wordmark = resolveSiteWordmark(report, effectiveBranding);
  // `resolveCoverKicker` is ALREADY type-aware ("<brand> · <shortLabel>"), so a
  // hardcoded "· BTM Feasibility Series" suffix would duplicate (feasibility) or
  // contradict (e.g. Environmental) the type — just append " Series".
  const seriesBrand = resolveCoverKicker(theme.companyName, report);
  const memoLabel = MEMO_LABEL_BY_TYPE[reportType] ?? 'Feasibility Memorandum';
  const preparedDate = resolvePreparedDate(report);

  // Subtitle = the LOCATION/SIZE byline (address · acres). The sponsor has its
  // OWN cell below, so the "Prepared for …" byline fragment is dropped here.
  const subtitle =
    resolveBylineParts(report, branding)
      .filter((p) => !/^Prepared for/i.test(p))
      .join(' · ')
    || `${seriesBrand} behind-the-meter power feasibility assessment`;
  // SPONSOR = the customer company — NOT byline[0] (which is the site ADDRESS).
  const sponsor = report.company?.name?.trim() || theme.companyName?.trim() || seriesBrand;

  // Verdict — canonical cover verdict + an italic description (BTM decision
  // summary) + the gating-conditions count; degrades cleanly when absent.
  const verdict = resolveCoverVerdict(report);
  const showVerdict = (verdict.tier !== 'none' || verdict.savedDecisionPending) && verdict.label !== '—';
  const verdictRole = verdictBoxRole(verdict.tier);
  const verdictDesc =
    firstLine(report.conclusion?.btmDecisionSummary)
    ?? firstLine((report as { btmDecisionSummary?: string }).btmDecisionSummary);
  const conditionsCount = report.conclusion?.keyConditions?.filter(
    (c) => typeof c === 'string' && c.trim().length > 0,
  ).length ?? 0;

  // 4-up headline metric strip — the shared chip machinery, non-null only,
  // capped at 4 (golden: FACILITY TARGET / PHASED RAMP / FIRM GRID / COD).
  const metricChips: CoverChip[] = resolveCoverChipsForReport(report, 'data-dense')
    .filter((c) => c.value != null && c.value.trim().length > 0)
    .slice(0, 4);

  const bodyFontSize = `calc(${reportTypographySizes(theme).body} * ${theme.bodyTextScale ?? 1})`;
  const logoUrl = effectiveBranding.primaryLogoUrl;

  return (
    <div
      className="relative flex flex-col"
      data-testid="cover-memo"
      data-cover-variant="memo"
      style={{
        color: roleColor(theme, 'body'),
        fontFamily: theme.bodyFont,
        minHeight: `max(${COVER_MIN_HEIGHT_PX}px, calc(var(--report-page-height, 297mm) - 14mm - 16mm - 1mm))`,
      }}
    >
      {/* ── masthead: logo (left) + CONFIDENTIAL · series · prepared (right) ── */}
      <div className="flex items-start justify-between">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={effectiveBranding.primaryLogoAlt}
            data-testid="cover-memo-logo"
            style={{ height: 30, width: 'auto', objectFit: 'contain' }}
            crossOrigin="anonymous"
          />
        ) : (
          <span
            className="uppercase"
            style={{ fontSize: 15, letterSpacing: '0.2em', fontFamily: theme.headingFont, color: reportHeadingInk(theme) }}
          >
            {seriesBrand}
          </span>
        )}
        <div className="text-right" style={{ lineHeight: 1.7 }}>
          <div
            className="text-[9.5px] font-bold uppercase tracking-[0.2em]"
            style={{ color: roleColor(theme, 'negative') }}
            data-testid="cover-memo-confidential"
          >
            Confidential
          </div>
          <div className="text-[9.5px] uppercase tracking-[0.16em]" style={{ color: roleColor(theme, 'muted') }}>
            Prepared {preparedDate}
          </div>
        </div>
      </div>
      <div style={{ borderTop: `1px solid ${roleColor(theme, 'rule')}`, marginTop: 16 }} />
      {/* GATEWAY POWER platform credit — right-aligned under the masthead rule,
          in the pt-12 gap above the memorandum label (zero height). */}
      <CoverHeaderPlatformCredit
        tone={platformCreditToneForTheme(theme)}
        color={roleColor(theme, 'muted')}
        testId="cover-memo-platform-credit"
      />

      {/* ── tracked memorandum label + title + italic subtitle ── */}
      <div className="pt-12">
        <div
          className="text-[10.5px] font-bold uppercase tracking-[0.26em]"
          style={{ color: roleColor(theme, 'accent') }}
          data-testid="cover-memo-label"
        >
          {memoLabel}
        </div>
        <h1
          id="cover-title"
          data-testid="cover-memo-title"
          style={{
            fontFamily: theme.headingFont,
            color: reportHeadingInk(theme),
            fontSize: `clamp(32px, 5vw, calc(${reportTypographySizes(theme).h2} * 2.3))`,
            fontWeight: 700,
            lineHeight: 1.04,
            letterSpacing: '-0.015em',
            margin: '14px 0 0',
            maxWidth: '20ch',
          }}
        >
          {title}
        </h1>
        <p
          className="italic"
          data-testid="cover-memo-subtitle"
          style={{ color: roleColor(theme, 'muted'), fontFamily: theme.headingFont, fontSize: `calc(${bodyFontSize} * 1.06)`, margin: '12px 0 0', maxWidth: '46ch' }}
        >
          {subtitle}
        </p>
      </div>

      {/* ── SITE / SPONSOR two-column meta ── */}
      <div
        className="grid grid-cols-2 gap-6 pt-9"
        data-testid="cover-memo-meta"
      >
        {([['Site', wordmark], ['Sponsor', sponsor]] as const).map(([label, value]) => (
          <div key={label} style={{ borderTop: `1px solid ${roleColorAlpha(theme, 'ink', 0.2)}`, paddingTop: 8 }}>
            <div className="text-[9px] font-bold uppercase tracking-[0.2em]" style={{ color: roleColor(theme, 'muted') }}>
              {label}
            </div>
            <div style={{ color: reportHeadingInk(theme), fontSize: bodyFontSize, marginTop: 4, fontWeight: 500 }}>
              {value}
            </div>
          </div>
        ))}
      </div>

      {/* ── HEADLINE VERDICT box — tier-coloured left border + filled wash ── */}
      {showVerdict && (
        <div
          className="flex items-center gap-5 mt-9"
          data-testid="cover-memo-verdict"
          data-verdict-tier={verdict.tier}
          style={{
            borderLeft: `4px solid ${roleColor(theme, verdictRole)}`,
            background: roleColorAlpha(theme, verdictRole, 0.07),
            borderRadius: 4,
            padding: '16px 20px',
          }}
        >
          <span
            className="font-bold uppercase tracking-[0.06em] whitespace-nowrap"
            style={{ color: roleColor(theme, verdictRole), fontFamily: theme.headingFont, fontSize: `calc(${bodyFontSize} * 1.55)` }}
            data-testid="cover-memo-verdict-label"
          >
            {verdict.label}
          </span>
          <span className="italic" style={{ color: roleColor(theme, 'body'), fontSize: bodyFontSize, lineHeight: 1.45 }}>
            {verdictDesc
              ?? (conditionsCount > 0
                ? `Conditional on ${conditionsCount} gating ${conditionsCount === 1 ? 'condition' : 'conditions'} addressed below.`
                : 'Evidence-justified verdict; see the conclusion for the supporting status table.')}
          </span>
        </div>
      )}

      {/* ── 4-up bordered METRIC strip ── */}
      {metricChips.length > 0 && (
        <div
          className="grid mt-9"
          data-testid="cover-memo-metrics"
          style={{
            gridTemplateColumns: `repeat(${metricChips.length}, minmax(0, 1fr))`,
            border: `1px solid ${roleColor(theme, 'rule')}`,
            borderRadius: 4,
            overflow: 'hidden',
          }}
        >
          {metricChips.map((chip, i) => (
            <div
              key={chip.id}
              data-testid={`cover-memo-metric-${chip.id}`}
              style={{
                padding: '14px 16px',
                borderLeft: i === 0 ? undefined : `1px solid ${roleColor(theme, 'rule')}`,
              }}
            >
              <div className="text-[8.5px] font-bold uppercase tracking-[0.16em]" style={{ color: roleColor(theme, 'muted') }}>
                {chip.label}
              </div>
              <div
                className="tabular-nums"
                style={{ color: reportHeadingInk(theme), fontFamily: theme.headingFont, fontSize: `calc(${bodyFontSize} * 1.5)`, fontWeight: 700, marginTop: 6, lineHeight: 1.1 }}
              >
                {chip.value}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── BRAND · SITE footer (bottom-pinned) ── */}
      <div
        className="mt-auto flex items-center justify-between pt-3"
        data-testid="cover-memo-footer"
        style={{ borderTop: `1px solid ${roleColorAlpha(theme, 'ink', 0.12)}` }}
      >
        <span className="text-[9px] uppercase tracking-[0.18em]" style={{ color: roleColor(theme, 'muted') }}>
          {seriesBrand} · {wordmark}
        </span>
        <span className="text-[8px]" style={{ color: roleColor(theme, 'faint') }}>
          {disclaimer && disclaimer.trim() && disclaimer.trim() !== REPORT_COVER_DISCLAIMER.trim()
            ? disclaimer
            : 'Confidential – directional guidance; validate independently.'}
        </span>
      </div>
    </div>
  );
};
